import { INTENT_TYPE } from '../../constants';
import { type ConvergenceRule, type FeatureContext } from '../types';

/**
 * `C-TERMINAL-QUESTION` — the query ends with a question mark.
 *
 * A trailing question mark is a deliberate final keystroke rather than incidental
 * punctuation, and no other rule in the catalog gets a signal that cheap: `why?`, `SEO？`,
 * `is it?`, and every non-English question have nothing else to distinguish them from a
 * search term. `S-EMPTY` is what makes acting on it safe — punctuation with no word-like
 * token, and a lone single letter, are claimed two rules into the catalog and never reach
 * here, so "ends with `?`" is only ever asked of input that has something to ask about.
 *
 * Three properties of its position carry the rest:
 *
 * - **Below the Search protections**, so it cannot override them: `"Launch plan?"` is a
 *   quoted title, and `go to the launch plan?` is still navigation.
 * - **Below every semantic grammar**, so the more specific reason is the one reported.
 *   `can Rovo search Confluence?` stays `AUX_QUESTION`, which keeps the per-rule
 *   telemetry that the bounded grammars exist to produce.
 * - **Above `S-SHORT`**, which is the whole point: `why?` and `SEO？` are one and two
 *   tokens, and the short fallback claimed both.
 *
 * Unlike the bounded grammars it carries **no `hasNonLatinLetter` guard**, deliberately.
 * The grammars are English-only because they read English word order; a terminal question
 * mark is punctuation, so it means the same thing in every script that uses it. NFKC folds
 * the full-width `？` to `?` before any rule runs, so a CJK keyboard and a US one behave
 * identically and `ロードマップとは？` is a question rather than residual traffic.
 *
 * Two boundaries are deliberate rather than overlooked:
 *
 * - **The mark must be terminal.** `is the roadmap? ready` is mid-edit text, not a
 *   question, and `why?!` ends in an exclamation — both fall through to the rules below.
 * - **Only `?` counts.** NFKC folds the full-width form to it, but not the Arabic `؟` or
 *   the Armenian `՞`, so those scripts still reach the residual layer. Widening the set is
 *   a lexicon change here, not a structural one.
 */
export const endsWithQuestionMark = (features: FeatureContext): boolean =>
	features.terminalPunctuation === '?';

export const terminalQuestionRule: ConvergenceRule = {
	type: INTENT_TYPE.CHAT,
	pattern: 'TERMINAL_QUESTION',
	matches: endsWithQuestionMark,
};
